import { useEffect, useState } from "react";
import api from "@/lib/api";

const inp = "w-full bg-card border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-[#3B82F6]/30";

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function FieldInput({ f, value, onChange }) {
  const v = value ?? f.default_value ?? (["multiselect", "checkboxes"].includes(f.type) ? [] : "");
  switch (f.type) {
    case "textarea":
      return <textarea rows={3} className={inp} placeholder={f.placeholder || ""} value={v} onChange={(e) => onChange(e.target.value)} data-testid={`custom-${f.key}`} />;
    case "number":
      return <input type="number" className={inp} placeholder={f.placeholder || ""} value={v ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={`custom-${f.key}`} />;
    case "date":
      return <input type="date" className={inp} value={v || ""} onChange={(e) => onChange(e.target.value)} data-testid={`custom-${f.key}`} />;
    case "boolean":
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" className="accent-[#1D4ED8]" checked={!!v} onChange={(e) => onChange(e.target.checked)} data-testid={`custom-${f.key}`} />
          <span className="text-muted-foreground">Sim</span>
        </label>
      );
    case "select":
      return (
        <select className={inp} value={v || ""} onChange={(e) => onChange(e.target.value)} data-testid={`custom-${f.key}`}>
          <option value="">—</option>
          {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label || o.value}</option>)}
        </select>
      );
    case "multiselect":
    case "checkboxes": {
      const arr = Array.isArray(v) ? v : [];
      return (
        <div className="flex flex-wrap gap-2" data-testid={`custom-${f.key}`}>
          {(f.options || []).map((o) => {
            const on = arr.includes(o.value);
            return (
              <button key={o.value} type="button"
                onClick={() => onChange(on ? arr.filter(x => x !== o.value) : [...arr, o.value])}
                className={`px-3.5 py-1.5 rounded-full text-xs border transition-all ${on ? "bg-primary text-foreground border-primary" : "bg-transparent text-muted-foreground border-border hover:border-primary/50"}`}>
                {o.label || o.value}
              </button>
            );
          })}
        </div>
      );
    }
    case "file":
      return (
        <div>
          <input type="file" className="block text-xs text-muted-foreground" data-testid={`custom-${f.key}`}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.size > 2_500_000) { alert("Ficheiro > 2.5MB não suportado nesta versão."); return; }
              const dataUrl = await readAsDataUrl(file);
              onChange({ name: file.name, size: file.size, type: file.type, data_url: dataUrl });
            }} />
          {v && typeof v === "object" && v.name && (
            <div className="text-xs text-muted-foreground mt-1">📎 {v.name}</div>
          )}
        </div>
      );
    default:
      return <input type="text" className={inp} placeholder={f.placeholder || ""} maxLength={f.max_length || undefined} value={v || ""} onChange={(e) => onChange(e.target.value)} data-testid={`custom-${f.key}`} />;
  }
}

/**
 * Renders the CUSTOM (non-standard) active fields for a given screen.
 * value: { [field_key]: any }
 * onChange: (nextValue) => void
 */
export default function CustomFieldsSection({ screen, value, onChange, title = "Campos adicionais" }) {
  const [fields, setFields] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/form-fields?screen=${screen}&active_only=true`)
      .then((r) => setFields((r.data || []).filter(f => !f.is_standard)))
      .finally(() => setLoaded(true));
  }, [screen]);

  const set = (key, v) => onChange({ ...(value || {}), [key]: v });

  if (!loaded) return null;
  if (fields.length === 0) return null;

  // group by section
  const groups = {};
  fields.forEach(f => {
    const k = f.section || "_default";
    if (!groups[k]) groups[k] = { label: f.section_label || null, items: [] };
    groups[k].items.push(f);
  });

  return (
    <div className="space-y-6" data-testid={`custom-section-${screen}`}>
      <div className="pt-4 border-t border-dashed border-border">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-semibold mb-4">{title}</div>
      </div>
      {Object.entries(groups).map(([k, g]) => (
        <div key={k}>
          {g.label && <div className="text-xs text-foreground font-semibold mb-3">{g.label}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {g.items.map(f => (
              <label key={f.id} className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
                  {f.label} {f.required && <span className="text-foreground">*</span>}
                </div>
                <FieldInput f={f} value={(value || {})[f.key]} onChange={(v) => set(f.key, v)} />
                {f.help_text && <div className="text-[10px] text-muted-foreground/70 mt-1">{f.help_text}</div>}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
